import { Box, Card, CardHeader, Checkbox, Chip, Divider, FormControlLabel, Grid, IconButton, Menu, MenuItem, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { useDropzone } from "react-dropzone";
import { enqueueSnackbar } from "notistack";
import SignatureCanvas from "react-signature-canvas";
import Iconify from "src/components/iconify/iconify";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxAutocompleteField from "src/components/inputs/trux-autocomplete-field";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import TruxSimpleDateTimeField from "src/components/inputs/trux-simple-date-time-field";
import { ModelSavingData } from "src/hooks/use-model-saving-data";
import { RentalCustomer, RentalDamageMarker, RentalOrganizationSettings, RentalTrailerEvent } from "src/models";
import axios, { endpoints, formatEndpoint } from "src/utils/axios";
import { Constants, RENTAL_DEFECT_OPTIONS, rentalTrailerType } from "src/constants";
import {
  RentalWheelDiagram,
  brakeSlotLabelsForType,
  brakeSlotRowsForType,
  emptyBrakeRows,
  emptyTire,
  tireFilled,
  tireSlotLabelsForType,
  tireSlotRowsForType,
} from "./rental-wheel-diagram";
import { RENTAL_TRAILER_DIAGRAMS } from "./trailer-diagrams";
import { GUIDED_PHOTO_SLOTS, GuidedPhotoSection } from "./guided-photo-section";
import { downscaleImageFile } from "./downscale-image";

export function emptyTireRows(trailerType?: string) {
  return Array.from({ length: tireSlotLabelsForType(trailerType).length }, () => emptyTire());
}

export function tiresConditionType(event: RentalTrailerEvent | undefined) {
  return event?.tires?.find((tire) => tire.conditionType !== "")?.conditionType || "rating";
}

export function brakesConditionType(event: RentalTrailerEvent | undefined) {
  return event?.brakes?.find((brake) => brake.conditionType !== "")?.conditionType || "rating";
}

// a remotely e-signed contract stores the customer's signature in the applied-signatures
// audit trail rather than the on-tablet signatureCustomer field
function customerSignatureUrl(event: RentalTrailerEvent) {
  if (event.signatureCustomer) {
    return event.signatureCustomer;
  }
  const applied = event.appliedContractInfo?.appliedSignatures;
  return applied?.length ? applied[applied.length - 1].signatureUrl : "";
}

export async function uploadSignatureCanvas(canvas: SignatureCanvas | null): Promise<string> {
  if (!canvas || canvas.isEmpty()) {
    return "";
  }
  const dataUrl = canvas.getCanvas().toDataURL("image/png");
  const blob = await fetch(dataUrl).then((r) => r.blob());
  const file = new File([blob], "signature.png", { type: "image/png" });
  const response = await axios.postForm(endpoints.rentalTrailerEvents.uploadFile, { file });
  return response.data.fileUrl || "";
}

// mirrors the backend's ValidateOutboundComplete; unconfigured settings require everything
function outboundCompleteErrors(event: RentalTrailerEvent, settings?: RentalOrganizationSettings): string[] {
  const errors: string[] = [];
  if (!event.rentalStartTime) {
    errors.push("Please set the rental Start time");
  }
  if (settings?.requirePhotos ?? true) {
    const missingSides = GUIDED_PHOTO_SLOTS.filter((slot) => !event.guidedPhotos?.[slot.key]).map(
      (slot) => slot.label
    );
    if (missingSides.length) {
      errors.push(`Please photograph the ${missingSides.join(", ")} of the unit`);
    }
  }

  const requireBrand = settings?.requireTireBrand ?? true;
  const requireSerial = settings?.requireTireSerial ?? true;
  const requireCondition = settings?.requireTireCondition ?? true;
  const requiredLabels = [
    requireBrand && "Brand",
    requireSerial && "Serial",
    requireCondition && "Condition",
  ].filter(Boolean);
  if (requiredLabels.length) {
    const tires = event.tires || [];
    const incomplete =
      !tires.length ||
      tires.some(
        (tire) =>
          (requireBrand && tire.brand === "") ||
          (requireSerial && tire.serial === "") ||
          (requireCondition && tire.condition === "")
      );
    if (incomplete) {
      errors.push(`Please record the ${requiredLabels.join(", ")} for every tire`);
    }
  }

  if (settings?.requireBrakes) {
    const brakes = event.brakes || [];
    if (!brakes.length || brakes.some((brake) => brake.condition === "")) {
      errors.push("Please record the Condition for every brake");
    }
  }
  return errors;
}

export async function submitRentalTrailerEvent(options: {
  eventModel: ModelSavingData<RentalTrailerEvent>;
  sigCanvas: SignatureCanvas | null;
  customerSigCanvas: SignatureCanvas | null;
  sendEmail: boolean;
  signOnSite?: boolean;
  requireOutboundComplete?: boolean;
  organizationSettings?: RentalOrganizationSettings;
}): Promise<RentalTrailerEvent | null> {
  const { eventModel, sigCanvas, customerSigCanvas, sendEmail, signOnSite, requireOutboundComplete, organizationSettings } = options;

  if (requireOutboundComplete && eventModel.model) {
    const errors = outboundCompleteErrors(eventModel.model, organizationSettings);
    errors.forEach((message) => enqueueSnackbar(message, { variant: "error" }));
    if (errors.length) {
      return null;
    }
  }

  if (signOnSite && !eventModel.model?.customerIdPhotos?.length) {
    enqueueSnackbar("Please photograph the Customer's ID", { variant: "error" });
    return null;
  }

  if (
    signOnSite &&
    (!customerSigCanvas || customerSigCanvas.isEmpty()) &&
    !eventModel.model?.signatureCustomer
  ) {
    enqueueSnackbar("The Customer needs to sign on the tablet first", { variant: "error" });
    return null;
  }

  try {
    // a drawn canvas replaces the stored signature; an empty one keeps it, so
    // re-saving a draft never wipes signatures collected earlier
    const signature = await uploadSignatureCanvas(sigCanvas);
    const signatureCustomer = await uploadSignatureCanvas(customerSigCanvas);
    eventModel.update((e) => {
      if (signature || eventModel.isNew) {
        e.signature = signature;
      }
      if (signatureCustomer || eventModel.isNew) {
        e.signatureCustomer = signatureCustomer;
      }
    });

    const response = await eventModel.save();
    if (!response) {
      return null;
    }
    let savedEvent = response.data as RentalTrailerEvent;

    // the event is already saved past this point — a failed send/finalize must
    // never look like a failed save, or a retry would create a duplicate event
    if (signOnSite) {
      try {
        const signResponse = await axios.post(
          formatEndpoint(endpoints.rentalTrailerEvents.signOnSite, { eventId: savedEvent.id })
        );
        savedEvent = signResponse.data as RentalTrailerEvent;
        enqueueSnackbar("Contract signed — a copy has been emailed to the Customer");
      } catch (error) {
        if (error?.response?.status === 422 && error.response.data?.length) {
          error.response.data.forEach((validation: { message: string }) =>
            enqueueSnackbar(validation.message, { variant: "error" })
          );
        } else {
          enqueueSnackbar("The contract could not be finalized. Please try again.", { variant: "error" });
        }
        enqueueSnackbar(
          "The form was saved but the contract was NOT finalized — fix the issue and try again from the saved form",
          { variant: "warning" }
        );
      }
    } else if (sendEmail) {
      try {
        await axios.post(
          formatEndpoint(endpoints.rentalTrailerEvents.sendContract, { eventId: savedEvent.id })
        );
        enqueueSnackbar("The Rental Agreement has been emailed to the Customer");
      } catch (error) {
        if (error?.response?.status === 422 && error.response.data?.length) {
          error.response.data.forEach((validation: { message: string }) =>
            enqueueSnackbar(validation.message, { variant: "error" })
          );
        } else {
          enqueueSnackbar("The Agreement could not be sent. Please try again.", { variant: "error" });
        }
        enqueueSnackbar(
          "The form was saved but the Agreement was NOT sent — fix the issue and use Send Contract on the saved form",
          { variant: "warning" }
        );
      }
    }

    return savedEvent;
  } catch (error) {
    if (error?.response?.status === 409) {
      enqueueSnackbar("This trailer was changed elsewhere — please reload and try again", {
        variant: "error",
      });
      return null;
    }
    throw error;
  }
}

function customerLabel(customer: RentalCustomer) {
  return `${customer.firstName} ${customer.lastName} (${customer.companyName})`;
}

const legendNumber = (index: number) => String(index + 1).padStart(2, "0");

export function rentalPeriodLabel(event: RentalTrailerEvent, timezone: string) {
  if (!event.rentalStartTime) {
    return "Not set";
  }
  const format = (iso: string) => DateTime.fromISO(iso).setZone(timezone).toFormat("MM/dd/yyyy HH:mm");
  return `${format(event.rentalStartTime)} – ${event.rentalEndTime ? format(event.rentalEndTime) : "Open"}`;
}

type DamageMarkerDiagramProps = {
  src: string;
  legendCodes: string[];
  markers: RentalDamageMarker[];
  editable: boolean;
  onChange: (markers: RentalDamageMarker[]) => void;
};

// keeps the marker dot fully inside the diagram edges (percent units)
const MARKER_EDGE_MARGIN = 3;
// markers closer than this (percent, center to center) would overlap
const MARKER_MIN_DISTANCE = 6;

function DamageMarkerDiagram({ src, legendCodes, markers, editable, onChange }: DamageMarkerDiagramProps) {
  const [pendingMark, setPendingMark] = useState<{ mouseX: number; mouseY: number; x: number; y: number } | null>(
    null
  );
  const diagramRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ index: number; startX: number; startY: number; moved: boolean } | null>(null);

  const markerPosition = (clientX: number, clientY: number) => {
    const rect = diagramRef.current?.getBoundingClientRect();
    if (!rect) {
      return null;
    }
    return {
      x: Math.min(100 - MARKER_EDGE_MARGIN, Math.max(MARKER_EDGE_MARGIN, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.min(100 - MARKER_EDGE_MARGIN, Math.max(MARKER_EDGE_MARGIN, ((clientY - rect.top) / rect.height) * 100)),
    };
  };

  const tooCloseToAnother = (position: { x: number; y: number }, excludeIndex: number) =>
    markers.some(
      (marker, index) =>
        index !== excludeIndex &&
        Math.hypot(marker.x - position.x, marker.y - position.y) < MARKER_MIN_DISTANCE
    );

  const onDiagramClick = (clickEvent: React.MouseEvent<HTMLElement>) => {
    if (!editable) {
      return;
    }
    const position = markerPosition(clickEvent.clientX, clickEvent.clientY);
    if (!position) {
      return;
    }
    if (tooCloseToAnother(position, -1)) {
      enqueueSnackbar("Markers cannot overlap — place it next to the existing marker", { variant: "warning" });
      return;
    }
    setPendingMark({ mouseX: clickEvent.clientX, mouseY: clickEvent.clientY, ...position });
  };

  const onMarkerPointerDown = (index: number) => (pointerEvent: React.PointerEvent<HTMLElement>) => {
    if (!editable) {
      return;
    }
    pointerEvent.stopPropagation();
    pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
    dragState.current = { index, startX: pointerEvent.clientX, startY: pointerEvent.clientY, moved: false };
  };

  const onMarkerPointerMove = (pointerEvent: React.PointerEvent<HTMLElement>) => {
    const drag = dragState.current;
    if (!drag) {
      return;
    }
    // a small dead zone keeps finger jitter from turning a tap into a drag
    if (!drag.moved && Math.hypot(pointerEvent.clientX - drag.startX, pointerEvent.clientY - drag.startY) < 4) {
      return;
    }
    drag.moved = true;
    const position = markerPosition(pointerEvent.clientX, pointerEvent.clientY);
    // a move onto another marker is not applied — the marker stops beside it
    if (position && !tooCloseToAnother(position, drag.index)) {
      onChange(markers.map((marker, markerIndex) => (markerIndex === drag.index ? { ...marker, ...position } : marker)));
    }
  };

  const onMarkerPointerUp = () => {
    const drag = dragState.current;
    dragState.current = null;
    if (drag && !drag.moved) {
      onChange(markers.filter((_, markerIndex) => markerIndex !== drag.index));
    }
  };

  return (
    <>
      <Box sx={{ maxWidth: 440, width: "100%", mx: "auto", bgcolor: "common.white", borderRadius: 1, p: 1 }}>
        <Box
          ref={diagramRef}
          position="relative"
          onClick={onDiagramClick}
          sx={{ cursor: editable ? "crosshair" : undefined }}
        >
          <Box component="img" src={src} alt="Trailer inspection diagram" sx={{ width: "100%", display: "block" }} />
          {markers.map((marker, index) => {
            const legendIndex = legendCodes.indexOf(marker.code);
            return (
              <Box
                // markers have no id; position + code repeats are legitimate
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                title={marker.code}
                onPointerDown={onMarkerPointerDown(index)}
                onPointerMove={onMarkerPointerMove}
                onPointerUp={onMarkerPointerUp}
                onClick={(clickEvent) => clickEvent.stopPropagation()}
                sx={{
                  position: "absolute",
                  left: `${marker.x}%`,
                  top: `${marker.y}%`,
                  transform: "translate(-50%, -50%)",
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: editable ? "grab" : "default",
                  touchAction: "none",
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: "error.main",
                    color: "common.white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    boxShadow: 2,
                  }}
                >
                  {legendIndex >= 0 ? legendIndex + 1 : "?"}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      {editable && (
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
          Tap the picture where the damage is and pick its condition. Drag a marker to move it, tap it to remove it.
        </Typography>
      )}
      <Menu
        open={!!pendingMark}
        onClose={() => setPendingMark(null)}
        anchorReference="anchorPosition"
        anchorPosition={pendingMark ? { top: pendingMark.mouseY, left: pendingMark.mouseX } : undefined}
      >
        {legendCodes.map((code, index) => (
          <MenuItem
            key={code}
            dense
            onClick={() => {
              if (pendingMark) {
                onChange([...markers, { code, x: pendingMark.x, y: pendingMark.y }]);
              }
              setPendingMark(null);
            }}
          >
            {`${legendNumber(index)} ${code}`}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function DamageLegend({ legendCodes, defects }: { legendCodes: string[]; defects: string[] }) {
  return (
    <Grid container>
      {legendCodes.map((code, index) => {
        const marked = defects.includes(code);
        return (
          <Grid item xs={6} sm={4} key={code}>
            <Typography
              variant="body2"
              sx={{ py: 0.25, color: marked ? "error.main" : "text.secondary", fontWeight: marked ? 700 : 400 }}
            >
              {`${legendNumber(index)} ${code}`}
            </Typography>
          </Grid>
        );
      })}
    </Grid>
  );
}

type PhotoSectionProps = {
  title: string;
  photos: string[];
  editable: boolean;
  onAdd: (fileUrl: string) => void;
  onRemove: (fileUrl: string) => void;
};

function PhotoSection({ title, photos, editable, onAdd, onRemove }: PhotoSectionProps) {
  const [savingFile, setSavingFile] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((accepted) => {
        setSavingFile(true);
        downscaleImageFile(accepted)
          .then((file) => axios.postForm(endpoints.rentalTrailerEvents.uploadFile, { file }))
          .then((response) => response.data)
          .then((response) => {
            if (response.fileUrl) {
              onAdd(response.fileUrl);
            }
          })
          .catch(() => {
            enqueueSnackbar("Your photo could not be uploaded. Please try again.", { variant: "error" });
          })
          .finally(() => {
            setSavingFile(false);
          });
      });
    },
    [onAdd]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"] },
  });

  const content = (
    <Stack gap={1}>
      {editable && (
        <Box
          {...getRootProps()}
          sx={{
            border: "1px dashed",
            borderColor: isDragActive ? "primary.main" : "divider",
            borderRadius: 1,
            p: 2,
            textAlign: "center",
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="body2" color="text.secondary">
            {savingFile ? "Uploading…" : "Tap to take a photo or drop images here"}
          </Typography>
        </Box>
      )}
      {photos.length === 0 && !editable && (
        <Typography variant="body2" color="text.disabled">
          No photos
        </Typography>
      )}
      <Box display="flex" flexWrap="wrap" gap={1}>
        {photos.map((photo) => (
          <Box key={photo} position="relative">
            <Box component="a" href={photo} target="_blank" rel="noopener">
              <Box component="img" src={photo} sx={{ width: 100, height: 100, objectFit: "cover", borderRadius: 1, display: "block" }} />
            </Box>
            {editable && (
              <IconButton
                size="small"
                onClick={() => onRemove(photo)}
                sx={{ position: "absolute", top: 0, right: 0, background: "rgba(0,0,0,0.4)", color: "white" }}
              >
                <Iconify icon="mingcute:close-line" width={16} />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>
    </Stack>
  );

  if (!title) {
    return content;
  }
  return <TruxFormField label={title}>{content}</TruxFormField>;
}

type SignatureFieldProps = {
  label: string;
  existingUrl: string;
  editable: boolean;
  canvasRef: RefObject<SignatureCanvas>;
};

// the canvas element needs a pixel width, so it is sized to its container and
// re-created (which clears the drawing) only when the container width changes
function ResponsiveSignatureCanvas({ canvasRef }: { canvasRef: RefObject<SignatureCanvas> }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return undefined;
    }
    const observer = new ResizeObserver(() => setWidth(Math.floor(element.clientWidth)));
    observer.observe(element);
    setWidth(Math.floor(element.clientWidth));
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{ width: "100%", border: "1px solid", borderColor: "divider", borderRadius: 1, background: "white" }}
    >
      {width > 0 && (
        <SignatureCanvas
          ref={canvasRef}
          penColor="black"
          canvasProps={{ width, height: 150, style: { display: "block" } }}
        />
      )}
    </Box>
  );
}

function SignatureField({ label, existingUrl, editable, canvasRef }: SignatureFieldProps) {
  const [redrawing, setRedrawing] = useState(false);
  const showSaved = !!existingUrl && !redrawing;

  return (
    <TruxFormField label={label}>
      {editable ? (
        <Stack gap={1} alignItems="flex-start" width="100%">
          {showSaved ? (
            <Stack gap={0.5} width="100%">
              <Box
                sx={{ width: "100%", border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "common.white", p: 1 }}
              >
                <Box component="img" src={existingUrl} sx={{ maxWidth: 200, display: "block" }} />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ cursor: "pointer" }}
                onClick={() => setRedrawing(true)}
              >
                Redraw
              </Typography>
            </Stack>
          ) : (
            <>
              <ResponsiveSignatureCanvas canvasRef={canvasRef} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ cursor: "pointer" }}
                onClick={() => canvasRef.current?.clear()}
              >
                Clear
              </Typography>
            </>
          )}
        </Stack>
      ) : (
        existingUrl && <Box component="img" src={existingUrl} sx={{ maxWidth: 300, borderRadius: 1 }} />
      )}
    </TruxFormField>
  );
}

export function OutboundConditionSummary({ outboundEvent }: { outboundEvent: RentalTrailerEvent }) {
  const trailerTypeDef = rentalTrailerType(outboundEvent.trailerType);
  const outboundDiagram = RENTAL_TRAILER_DIAGRAMS[outboundEvent.trailerType];
  const legendCodes = trailerTypeDef?.damageCodes?.length ? trailerTypeDef.damageCodes : RENTAL_DEFECT_OPTIONS;
  const showMarkedDiagram = !!outboundDiagram && !!outboundEvent.damageMarkers?.length;
  const summaryDefects = showMarkedDiagram
    ? (outboundEvent.defects || []).filter((defect) => !legendCodes.includes(defect))
    : outboundEvent.defects || [];

  return (
    <Stack
      gap={1}
      sx={{ p: 2, mb: 1, borderRadius: 1, bgcolor: "action.hover" }}
    >
      <Typography variant="subtitle1">
        {`Outbound Condition — ${outboundEvent.eventTime ? DateTime.fromISO(outboundEvent.eventTime).toFormat("MM/dd/yyyy") : ""}`}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Compare the trailer against how it left the yard
      </Typography>

      {showMarkedDiagram && (
        <Box>
          <DamageMarkerDiagram
            src={outboundDiagram}
            legendCodes={legendCodes}
            markers={outboundEvent.damageMarkers || []}
            editable={false}
            onChange={() => {}}
          />
          <DamageLegend legendCodes={legendCodes} defects={outboundEvent.defects || []} />
        </Box>
      )}

      {trailerTypeDef?.usageFields && (
        <Box display="flex" flexWrap="wrap" gap={0.5}>
          {[
            ...trailerTypeDef.usageFields.map(({ label, field }) => [label, outboundEvent[field]]),
            ...(outboundEvent.equipment || []).map((item) => [item.name, item.value]),
          ]
            .filter(([, value]) => value)
            .map(([label, value]) => (
              <Chip key={label} size="small" label={`${label}: ${value}`} />
            ))}
        </Box>
      )}

      {!!outboundEvent.photos?.length && (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {outboundEvent.photos.map((photo) => (
            <Box key={photo} component="a" href={photo} target="_blank" rel="noopener">
              <Box
                component="img"
                src={photo}
                sx={{ width: 80, height: 80, objectFit: "cover", borderRadius: 1 }}
              />
            </Box>
          ))}
        </Box>
      )}

      {(!!outboundEvent.tires?.some(tireFilled) || !!outboundEvent.brakes?.some(tireFilled)) && (
        <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center">
          {!!outboundEvent.tires?.some(tireFilled) && (
            <Box sx={{ maxWidth: 420, minWidth: 300, flex: 1, display: "flex", justifyContent: "center" }}>
              <Box sx={{ width: "fit-content" }}>
                <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: "0.1em", display: "block" }}>
                  Tires
                </Typography>
                <RentalWheelDiagram
                  conditionType={tiresConditionType(outboundEvent)}
                  editable={false}
                  rows={outboundEvent.tires || []}
                  onChange={() => {}}
                  slotLabels={tireSlotLabelsForType(outboundEvent.trailerType)}
                  slotRows={tireSlotRowsForType(outboundEvent.trailerType)}
                />
              </Box>
            </Box>
          )}

          {!!outboundEvent.brakes?.some(tireFilled) && (
            <Box sx={{ maxWidth: 420, minWidth: 300, flex: 1, display: "flex", justifyContent: "center" }}>
              <Box sx={{ width: "fit-content" }}>
                <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: "0.1em", display: "block" }}>
                  Brakes
                </Typography>
                <RentalWheelDiagram
                  conditionType={brakesConditionType(outboundEvent)}
                  editable={false}
                  rows={outboundEvent.brakes || []}
                  onChange={() => {}}
                  itemLabel="Brake"
                  hideSerial
                  slotLabels={brakeSlotLabelsForType(outboundEvent.trailerType)}
                  slotRows={brakeSlotRowsForType(outboundEvent.trailerType)}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}

      {!!summaryDefects.length && (
        <Box display="flex" flexWrap="wrap" gap={0.5}>
          {summaryDefects.map((defect) => (
            <Chip key={defect} size="small" color="warning" label={defect} />
          ))}
        </Box>
      )}

      {outboundEvent.comment && (
        <Typography variant="body2" color="text.secondary">
          {outboundEvent.comment}
        </Typography>
      )}
    </Stack>
  );
}

export type RentalTrailerEventFormProps = {
  eventModel: ModelSavingData<RentalTrailerEvent>;
  rentalCustomers: RentalCustomer[];
  sigCanvas: RefObject<SignatureCanvas>;
  customerSigCanvas: RefObject<SignatureCanvas>;
  completeOutbound: boolean;
  setCompleteOutbound: (value: boolean) => void;
  confirmSendEmail: boolean;
  setConfirmSendEmail: (value: boolean) => void;
  signOnSite?: boolean;
  setSignOnSite?: (value: boolean) => void;
  outboundEvent?: RentalTrailerEvent;
  // drives how tire conditions are recorded and which fields are required
  organizationSettings?: RentalOrganizationSettings;
  variant?: "dialog" | "page";
};

type FormSectionProps = {
  variant: "dialog" | "page";
  title: string;
  subheader?: string;
  children: React.ReactNode;
};

function FormSection({ variant, title, subheader, children }: FormSectionProps) {
  if (variant === "page") {
    return (
      <Card>
        <CardHeader title={title} subheader={subheader} />
        <Stack gap={2} p={2.5} pt={2}>
          {children}
        </Stack>
      </Card>
    );
  }
  return (
    <Box>
      <Typography variant="subtitle1">{title}</Typography>
      {subheader && (
        <Typography variant="caption" color="text.secondary" display="block">
          {subheader}
        </Typography>
      )}
      <Stack gap={1.5} pt={1.5}>
        {children}
      </Stack>
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}

type SigningChoice = "email" | "onsite";

function customerSubheader(isNew: boolean, isOut: boolean) {
  if (!isNew) {
    return undefined;
  }
  return isOut ? "Who is renting the trailer" : "Returning the trailer";
}

export function RentalTrailerEventForm({
  eventModel,
  rentalCustomers,
  sigCanvas,
  customerSigCanvas,
  completeOutbound,
  setCompleteOutbound,
  confirmSendEmail,
  setConfirmSendEmail,
  signOnSite,
  setSignOnSite,
  outboundEvent,
  organizationSettings,
  variant = "dialog",
}: RentalTrailerEventFormProps) {
  const event = eventModel.model;
  if (!event) {
    return null;
  }

  const isOut = event.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_OUT;
  const { isNew } = eventModel;
  // an event whose send/finalize failed stays a draft, so the form remains
  // editable for the retry; sent or signed contracts are read-only
  const editable =
    isNew || event.rentalTrailerEventStatus === Constants.RENTAL_TRAILER_EVENT_STATUS_DRAFT;
  const trailerTypeDef = rentalTrailerType(event.trailerType);
  const damageCodes = trailerTypeDef?.damageCodes;
  const legendCodes = damageCodes?.length ? damageCodes : RENTAL_DEFECT_OPTIONS;
  // defects not in the legend come from free text (legacy events, mobile app)
  const freeTextDefects = (event.defects || []).filter((defect) => !legendCodes.includes(defect));

  const onMarkersChange = (markers: RentalDamageMarker[]) =>
    eventModel.update((e) => {
      e.damageMarkers = markers;
      const markerCodes = Array.from(new Set(markers.map((marker) => marker.code)));
      e.defects = [...markerCodes, ...(e.defects || []).filter((defect) => !legendCodes.includes(defect))];
    });

  const equipmentValue = (name: string) =>
    (event.equipment || []).find((item) => item.name === name)?.value || "";
  const setEquipmentValue = (name: string, value: string) =>
    eventModel.update((e) => {
      const items = (e.equipment || []).filter((item) => item.name !== name);
      e.equipment = value ? [...items, { name, value }] : items;
    });
  const trailerDiagram = RENTAL_TRAILER_DIAGRAMS[event.trailerType];

  const customerOptions = rentalCustomers.map((customer) => ({
    id: customer.id,
    label: customerLabel(customer),
  }));
  const selectedCustomer = customerOptions.find((option) => option.id === event.customerId) || {
    id: 0,
    label: "None",
  };
  const selectedCustomerDetails = rentalCustomers.find((customer) => customer.id === event.customerId);

  const signingChoice: SigningChoice = signOnSite ? "onsite" : "email";

  const onSigningChoice = (choice: SigningChoice) => {
    setCompleteOutbound(true);
    setConfirmSendEmail(choice === "email");
    setSignOnSite?.(choice === "onsite");
  };

  const showCustomerSignature = editable
    ? !isOut || signingChoice === "onsite" || !!customerSignatureUrl(event)
    : !!customerSignatureUrl(event);

  const organizationTimezone = organizationSettings?.companyTimeZone || "America/Chicago";

  return (
    <Stack direction="column" gap={variant === "page" ? 2.5 : 2}>
      {!isOut && isNew && outboundEvent && <OutboundConditionSummary outboundEvent={outboundEvent} />}

      {isOut && (
        <FormSection
          variant={variant}
          title="Rental Period"
          subheader={
            editable
              ? `Times are in ${organizationTimezone} — leave the end empty for an open-ended rental`
              : undefined
          }
        >
          {editable ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TruxSimpleDateTimeField
                  label="Start *"
                  timezone={organizationTimezone}
                  limitWidth={false}
                  value={event.rentalStartTime || undefined}
                  onChange={(date) =>
                    eventModel.update((e) => {
                      e.rentalStartTime = date || null;
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <TruxSimpleDateTimeField
                    label="End"
                    timezone={organizationTimezone}
                    limitWidth={false}
                    value={event.rentalEndTime || undefined}
                    onChange={(date) =>
                      eventModel.update((e) => {
                        e.rentalEndTime = date || null;
                      })
                    }
                  />
                  {event.rentalEndTime && (
                    <IconButton
                      size="small"
                      title="Make the rental open-ended"
                      onClick={() =>
                        eventModel.update((e) => {
                          e.rentalEndTime = null;
                        })
                      }
                    >
                      <Iconify icon="mingcute:close-line" width={18} />
                    </IconButton>
                  )}
                </Stack>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="subtitle1">
              {`${rentalPeriodLabel(event, organizationTimezone)} (${organizationTimezone})`}
            </Typography>
          )}
        </FormSection>
      )}

      <FormSection
        variant={variant}
        title={editable && isOut ? "Customer *" : "Customer"}
        subheader={customerSubheader(isNew, isOut)}
      >
        {editable && isOut ? (
          <>
            <TruxAutocompleteField
              options={customerOptions}
              selected={selectedCustomer}
              freeSolo={false}
              disablePortal={false}
              noOptionsText="Please add a Customer first"
              onChange={(selection) => {
                if (!selection) {
                  return;
                }
                const customer = rentalCustomers.find((c) => c.id === selection.id);
                if (customer) {
                  eventModel.update((e) => {
                    e.customerId = customer.id;
                    e.customerCompanyName = customer.companyName;
                    e.customerFirstName = customer.firstName;
                    e.customerLastName = customer.lastName;
                    e.customerAddress = customer.address;
                    e.customerPhone = customer.phone;
                    e.customerEmail = customer.email;
                    e.customerComment = customer.comment;
                  });
                }
              }}
              onClear={() => {
                eventModel.update((e) => {
                  e.customerId = 0;
                  e.customerCompanyName = "";
                  e.customerFirstName = "";
                  e.customerLastName = "";
                  e.customerPhone = "";
                  e.customerEmail = "";
                  e.customerComment = "";
                });
              }}
            />
            {selectedCustomerDetails && (
              <Stack direction="row" gap={1} flexWrap="wrap">
                {selectedCustomerDetails.email && (
                  <Chip
                    size="small"
                    icon={<Iconify icon="ic:round-email" width={14} />}
                    label={selectedCustomerDetails.email}
                    sx={{ bgcolor: "grey.200", color: "text.primary", "& .MuiChip-icon": { color: "text.secondary" } }}
                  />
                )}
                {selectedCustomerDetails.phone && (
                  <Chip
                    size="small"
                    icon={<Iconify icon="solar:phone-bold" width={14} />}
                    label={selectedCustomerDetails.phone}
                    sx={{ bgcolor: "grey.200", color: "text.primary", "& .MuiChip-icon": { color: "text.secondary" } }}
                  />
                )}
                {selectedCustomerDetails.companyName && (
                  <Chip
                    size="small"
                    label={selectedCustomerDetails.companyName}
                    sx={{ bgcolor: "grey.200", color: "text.primary" }}
                  />
                )}
              </Stack>
            )}
          </>
        ) : (
          <Typography>{`${event.customerFirstName} ${event.customerLastName} (${event.customerCompanyName})`}</Typography>
        )}
      </FormSection>

      <FormSection
        variant={variant}
        title={isOut ? "Photos" : "Return Photos"}
        subheader={editable ? "Photograph each side of the trailer" : undefined}
      >
        {isOut && editable ? (
          <GuidedPhotoSection
            photos={event.photos || []}
            guidedPhotos={event.guidedPhotos || { front: "", rear: "", left: "", right: "" }}
            onAdd={(fileUrl) =>
              eventModel.update((e) => {
                e.photos = [...(e.photos || []), fileUrl];
              })
            }
            onRemove={(fileUrl) =>
              eventModel.update((e) => {
                e.photos = (e.photos || []).filter((p) => p !== fileUrl);
              })
            }
            onSlotChange={(side, fileUrl) =>
              eventModel.update((e) => {
                e.guidedPhotos = {
                  ...(e.guidedPhotos || { front: "", rear: "", left: "", right: "" }),
                  [side]: fileUrl,
                };
              })
            }
          />
        ) : (
          <PhotoSection
            title=""
            editable={editable}
            photos={(isOut ? event.photos : event.returnPhotos) || []}
            onAdd={(fileUrl) =>
              eventModel.update((e) => {
                if (isOut) {
                  e.photos = [...(e.photos || []), fileUrl];
                } else {
                  e.returnPhotos = [...(e.returnPhotos || []), fileUrl];
                }
              })
            }
            onRemove={(fileUrl) =>
              eventModel.update((e) => {
                if (isOut) {
                  e.photos = (e.photos || []).filter((p) => p !== fileUrl);
                } else {
                  e.returnPhotos = (e.returnPhotos || []).filter((p) => p !== fileUrl);
                }
              })
            }
          />
        )}
        <TruxSimpleTextField
          label="Photo notes"
          value={isOut ? event.prePhotoText : event.afterPhotoText}
          disabled={!editable}
          onChange={(value) =>
            eventModel.update((e) => {
              if (isOut) {
                e.prePhotoText = value;
              } else {
                e.afterPhotoText = value;
              }
            })
          }
        />
      </FormSection>

      {isOut && (editable || !!event.customerIdPhotos?.length) && (
        <FormSection
          variant={variant}
          title="Customer ID"
          subheader={
            editable
              ? "Required when finalizing on site — customers signing by email upload their ID themselves"
              : undefined
          }
        >
          <PhotoSection
            title=""
            editable={editable}
            photos={event.customerIdPhotos || []}
            onAdd={(fileUrl) =>
              eventModel.update((e) => {
                e.customerIdPhotos = [...(e.customerIdPhotos || []), fileUrl];
              })
            }
            onRemove={(fileUrl) =>
              eventModel.update((e) => {
                e.customerIdPhotos = (e.customerIdPhotos || []).filter((p) => p !== fileUrl);
              })
            }
          />
        </FormSection>
      )}

      {trailerTypeDef?.usageFields && (
        <FormSection variant={variant} title="Usage & Equipment">
          <Box>
            <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: "0.1em", display: "block", mb: 1 }}>
              Usage
            </Typography>
            <Grid container spacing={1.5}>
              {trailerTypeDef.usageFields.map(({ label, field }) => (
                <Grid item xs={6} sm={4} md={2.4} key={field}>
                  <TruxSimpleTextField
                    label={label}
                    value={event[field]}
                    disabled={!editable}
                    onChange={(value) =>
                      eventModel.update((e) => {
                        e[field] = value;
                      })
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box>
            <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: "0.1em", display: "block", mb: 1 }}>
              Equipment Check
            </Typography>
            <Grid container spacing={1.5}>
              {(trailerTypeDef.equipmentItems || []).map((name) => (
                <Grid item xs={12} sm={6} md={4} key={name}>
                  <TruxSimpleTextField
                    label={name}
                    value={equipmentValue(name)}
                    disabled={!editable}
                    onChange={(value) => setEquipmentValue(name, value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </FormSection>
      )}

      <FormSection
        variant={variant}
        title="Condition"
        subheader={editable ? "Tap a wheel to record its brand, serial and condition" : undefined}
      >
        {trailerDiagram && (
          <>
            <DamageMarkerDiagram
              src={trailerDiagram}
              legendCodes={legendCodes}
              markers={event.damageMarkers || []}
              editable={editable}
              onChange={onMarkersChange}
            />
            <TruxFormField label={damageCodes?.length ? "Damage Conditions" : "Defects"}>
              <Box width="100%">
                <DamageLegend legendCodes={legendCodes} defects={event.defects || []} />
                {!!freeTextDefects.length && (
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                    {freeTextDefects.map((value) => (
                      <Chip key={value} label={value} size="small" color="warning" />
                    ))}
                  </Box>
                )}
              </Box>
            </TruxFormField>
          </>
        )}

        <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center">
          <Box sx={{ maxWidth: 480, minWidth: 300, flex: 1, display: "flex", justifyContent: "center" }}>
            <Box sx={{ width: "fit-content" }}>
              <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: "0.1em", display: "block" }}>
                Tires
              </Typography>
              <RentalWheelDiagram
                conditionType={editable ? organizationSettings?.tireConditionType || "rating" : tiresConditionType(event)}
                editable={editable}
                rows={event.tires || emptyTireRows(event.trailerType)}
                onChange={(rows) =>
                  eventModel.update((e) => {
                    e.tires = rows;
                  })
                }
                slotLabels={tireSlotLabelsForType(event.trailerType)}
                slotRows={tireSlotRowsForType(event.trailerType)}
              />
            </Box>
          </Box>

          <Box sx={{ maxWidth: 480, minWidth: 300, flex: 1, display: "flex", justifyContent: "center" }}>
            <Box sx={{ width: "fit-content" }}>
              <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: "0.1em", display: "block" }}>
                Brakes
              </Typography>
              <RentalWheelDiagram
                conditionType={editable ? "rating" : brakesConditionType(event)}
                editable={editable}
                rows={event.brakes || emptyBrakeRows(event.trailerType)}
                onChange={(rows) =>
                  eventModel.update((e) => {
                    e.brakes = rows;
                  })
                }
                itemLabel="Brake"
                hideSerial
                slotLabels={brakeSlotLabelsForType(event.trailerType)}
                slotRows={brakeSlotRowsForType(event.trailerType)}
              />
            </Box>
          </Box>
        </Box>

        {!trailerDiagram &&
          (editable ? (
            <TruxFormField label={damageCodes?.length ? "Damage Conditions" : "Defects"}>
              <Grid container>
                {legendCodes.map((code) => (
                  <Grid item xs={6} sm={4} key={code}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={(event.defects || []).includes(code)}
                          onChange={(_, checked) =>
                            eventModel.update((ev) => {
                              ev.defects = checked
                                ? [...(ev.defects || []), code]
                                : (ev.defects || []).filter((defect) => defect !== code);
                            })
                          }
                        />
                      }
                      label={code}
                    />
                  </Grid>
                ))}
              </Grid>
            </TruxFormField>
          ) : (
            <TruxFormField label={damageCodes?.length ? "Damage Conditions" : "Defects"}>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {(event.defects || []).map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
                {!(event.defects || []).length && (
                  <Typography variant="body2" color="text.secondary">
                    None reported
                  </Typography>
                )}
              </Box>
            </TruxFormField>
          ))}

        <TruxSimpleTextField
          label="Comment"
          value={event.comment}
          disabled={!editable}
          onChange={(value) =>
            eventModel.update((e) => {
              e.comment = value;
            })
          }
        />
      </FormSection>

      <FormSection
        variant={variant}
        title="Signatures"
        subheader={undefined}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SignatureField
              label="Yard Staff *"
              editable={editable}
              existingUrl={event.signature}
              canvasRef={sigCanvas}
            />
          </Grid>
          {showCustomerSignature && (
            <Grid item xs={12} md={6}>
              <SignatureField
                label={editable && isOut ? "Customer *" : "Customer"}
                editable={editable}
                existingUrl={customerSignatureUrl(event)}
                canvasRef={customerSigCanvas}
              />
            </Grid>
          )}
        </Grid>
      </FormSection>

      {isOut && isNew && (
        <FormSection variant={variant} title="Contract" subheader="How should the rental agreement be signed?">
          <RadioGroup value={signingChoice} onChange={(e) => onSigningChoice(e.target.value as SigningChoice)}>
            <FormControlLabel
              value="email"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">Email a signing link to the customer</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {event.customerEmail
                      ? `The agreement will be sent to ${event.customerEmail}`
                      : "The selected customer needs an email address"}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="onsite"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">Customer is here - finalize with the signature above</Typography>
                  <Typography variant="caption" color="text.secondary">
                    The customer signature drawn above becomes the contract signature
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormSection>
      )}
    </Stack>
  );
}
